import { ref, computed } from 'vue';
import Auth from 'dashboard/api/auth';

/**
 * Etapas do funil da empresa (slug -> nome legível + cor), para o cartão da
 * conversa pintar a bolinha da etapa.
 *
 * A conversa já traz o SLUG da etapa entre as etiquetas (`contato-inicial`),
 * mas o nome e a cor vivem no Aceleres e mudam por empresa. Sem isto o cartão
 * teria de inventar uma cor — que é justamente o que se quer evitar.
 *
 * Cache no módulo, uma busca por conta enquanto a aba viver: a lista muda
 * quando um gestor edita o funil, e a lista de conversas re-renderiza o tempo
 * todo. Uma busca por cartão seria centenas de chamadas iguais.
 */
const stagesByAccount = ref({});
const funnelsByAccount = ref({});
const inFlight = new Map();

const funnelsFromStages = stages => {
  const grouped = new Map();
  stages.forEach(stage => {
    const funnelId = stage?.funnel_id ?? stage?.funnelId;
    if (funnelId === undefined || funnelId === null) return;

    const id = String(funnelId);
    if (!grouped.has(id)) {
      grouped.set(id, {
        id: funnelId,
        name: stage.funnel_name || stage.funnelName || `Funil ${funnelId}`,
        stages: [],
      });
    }
    grouped.get(id).stages.push(stage);
  });
  return [...grouped.values()];
};

const authHeaders = () => {
  const authData = Auth.getAuthData();
  if (!authData) return {};

  const {
    'access-token': accessToken,
    'token-type': tokenType,
    client,
    expiry,
    uid,
  } = authData;

  return {
    'access-token': accessToken,
    'token-type': tokenType,
    client,
    expiry,
    uid,
  };
};

const fetchStages = async accountId => {
  const response = await fetch(
    `/api/v1/accounts/${accountId}/aceleres/funil_stages`,
    {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...authHeaders() },
    }
  );
  const data = await response.json().catch(() => ({}));
  return {
    stages: Array.isArray(data.stages) ? data.stages : [],
    funnels:
      Array.isArray(data.funnels) && data.funnels.length
        ? data.funnels
        : funnelsFromStages(Array.isArray(data.stages) ? data.stages : []),
  };
};

export function useFunilStages(accountId) {
  const load = () => {
    const id = Number(accountId?.value ?? accountId);
    if (!id || stagesByAccount.value[id] || inFlight.has(id)) return;

    // Falha vira lista vazia: sem etapas o cartão só não desenha a bolinha.
    // Um enfeite não deve derrubar a lista de conversas.
    const promise = fetchStages(id)
      .catch(() => ({ stages: [], funnels: [] }))
      .then(data => {
        stagesByAccount.value = {
          ...stagesByAccount.value,
          [id]: data.stages,
        };
        funnelsByAccount.value = {
          ...funnelsByAccount.value,
          [id]: data.funnels,
        };
        inFlight.delete(id);
      });

    inFlight.set(id, promise);
  };

  const refresh = () => {
    const id = Number(accountId?.value ?? accountId);
    if (!id) return;
    delete stagesByAccount.value[id];
    delete funnelsByAccount.value[id];
    inFlight.delete(id);
    load();
  };

  const stages = computed(() => {
    const id = Number(accountId?.value ?? accountId);
    return stagesByAccount.value[id] || [];
  });

  const funnels = computed(() => {
    const id = Number(accountId?.value ?? accountId);
    return funnelsByAccount.value[id] || [];
  });

  const stagesForFunnel = funnelId => {
    const funnel = funnels.value.find(
      item => String(item.id) === String(funnelId)
    );
    if (!funnel) return [];
    if (Array.isArray(funnel.stages)) return funnel.stages;

    const rawStageIds = funnel.stage_ids || funnel.stageIds || [];
    const stageIds = new Set(
      (Array.isArray(rawStageIds) ? rawStageIds : []).map(String)
    );
    return stages.value.filter(stage => stageIds.has(String(stage.id)));
  };

  /**
   * Etapa em que a conversa está, a partir das etiquetas dela.
   *
   * Quando há mais de uma etiqueta de etapa (acontece: o Chatwoot entrega em
   * ordem antiga -> nova), vale a MAIS AVANÇADA. É a mesma regra do
   * `pullChatwootToCard` no Aceleres — divergir dela faria o cartão mostrar
   * uma etapa e o funil outra.
   */
  const stageForLabels = labels => {
    const titles = Array.isArray(labels) ? labels : [];
    if (!titles.length || !stages.value.length) return null;

    return (
      stages.value
        .filter(stage => titles.includes(stage.slug))
        .sort((a, b) => Number(b.ordem) - Number(a.ordem))[0] || null
    );
  };

  /** Nome legível de uma etiqueta: o da etapa quando for etapa, senão o próprio. */
  const labelDisplayName = title => {
    const stage = stages.value.find(item => item.slug === title);
    return stage ? stage.nome : title;
  };

  return {
    stages,
    funnels,
    load,
    refresh,
    stagesForFunnel,
    stageForLabels,
    labelDisplayName,
  };
}

export default useFunilStages;
